import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { branchesAPI, showtimesAPI, moviesAPI } from '../api';
import { MapPin, Phone, Mail, Building, Calendar, Clock, ExternalLink, ArrowLeft } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const SalonDetail = () => {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [salon, setSalon] = useState(null);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMovies, setLoadingMovies] = useState(false);

  useEffect(() => {
    loadSalon();
  }, [slug]);

  // Update title when salon data loads
  useEffect(() => {
    if (salon) {
      document.title = `${salon.name} - ${settings.site_name || 'Cinefora'}`;
    }
  }, [salon, settings.site_name]);

  const loadSalon = async () => {
    try {
      setLoading(true);
      // Tüm salonları al ve slug ile eşleşeni bul
      const response = await branchesAPI.getAll();
      const foundSalon = response.data.find(b => b.slug === slug || b._id === slug);
      
      if (foundSalon) {
        setSalon(foundSalon);
        loadTodayMovies(foundSalon._id);
      }
    } catch (error) {
      console.error('Error loading salon:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodayMovies = async (salonId) => {
    try {
      setLoadingMovies(true);
      const today = new Date().toISOString().split('T')[0];
      
      const showtimesResponse = await showtimesAPI.getAll({
        branch_id: salonId,
        show_date: today
      });
      
      const showtimes = showtimesResponse.data;

      if (showtimes.length === 0) {
        setMovies([]);
        setLoadingMovies(false);
        return;
      }

      const movieIds = [...new Set(showtimes.map(st => st.movie_id))];
      
      const moviesData = await Promise.all(
        movieIds.map(async (movieId) => {
          try {
            const movieResponse = await moviesAPI.getById(movieId);
            const movie = movieResponse.data;
            
            // Arşivlenmiş filmleri atla
            if (movie.status === 'archived') {
              return null;
            }
            
            const movieShowtimes = showtimes.filter(st => st.movie_id === movieId);
            
            // Format ve dil bazlı gruplama
            const showtimesByFormatAndLanguage = movieShowtimes.reduce((acc, st) => {
              const format = st.format || 'Standart';
              const language = st.language || 'Türkçe Dublaj';
              const key = `${format} - ${language}`;
              
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(st.show_time);
              return acc;
            }, {});

            return {
              ...movie,
              showtimesByFormatAndLanguage
            };
          } catch (error) {
            console.error(`Error loading movie ${movieId}:`, error);
            return null;
          }
        })
      );

      // Null olmayan ve arşivlenmemiş filmleri filtrele
      setMovies(moviesData.filter(m => m !== null));
    } catch (error) {
      console.error('Error loading showtimes:', error);
    } finally {
      setLoadingMovies(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="container mx-auto px-6 py-32">
          <p className="text-white text-center">Yükleniyor...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!salon) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="container mx-auto px-6 py-32">
          <p className="text-white text-center">Salon bulunamadı</p>
          <div className="text-center mt-4">
            <Link to="/salonlarimiz" className="text-[#FDB714] hover:text-white">
              ← Salonlara Dön
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>{salon.name} - {settings.site_name || 'Cinefora'}</title>
        <meta name="description" content={`${salon.name} - ${salon.city}. Adres, iletişim bilgileri ve bugünkü seanslar.`} />
      </Helmet>
      <Header />
      
      <div className="container mx-auto px-6 py-32">
        {/* Back Button */}
        <Link 
          to="/salonlarimiz"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Salonlara Dön
        </Link>

        {/* Salon Hero */}
        <div className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Sol: Salon Bilgileri */}
            <div className="p-8 lg:p-12">
              <h1 className="text-4xl font-bold text-white mb-6">{salon.name}</h1>
              
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <MapPin size={24} className="text-[#FDB714] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white mb-1">{salon.city}</p>
                    <p>{salon.address}</p>
                  </div>
                </div>

                {salon.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={24} className="text-[#FDB714] flex-shrink-0" />
                    <a href={`tel:${salon.phone}`} className="hover:text-white transition-colors">
                      {salon.phone}
                    </a>
                  </div>
                )}

                {salon.email && (
                  <div className="flex items-center gap-3">
                    <Mail size={24} className="text-[#FDB714] flex-shrink-0" />
                    <a href={`mailto:${salon.email}`} className="hover:text-white transition-colors break-all">
                      {salon.email}
                    </a>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Building size={24} className="text-[#FDB714] flex-shrink-0" />
                  <span>{salon.halls_count} Salon</span>
                </div>

                {salon.map_url && (
                  <div className="pt-4">
                    <a
                      href={salon.map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FDB714] hover:bg-[#e5a510] text-black font-bold px-6 py-3 rounded-lg transition-colors"
                    >
                      <ExternalLink size={20} />
                      Haritada Gör
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Sağ: Salon Görseli */}
            {salon.image && (
              <div className="h-64 lg:h-auto">
                <img 
                  src={salon.image}
                  alt={salon.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x600?text=Salon';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Bugünkü Filmler */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
            <Calendar className="text-[#FDB714]" size={32} />
            Bugün Oynayan Filmler
          </h2>

          {loadingMovies ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FDB714] mx-auto"></div>
            </div>
          ) : movies.length === 0 ? (
            <div className="text-center py-12 bg-gray-900 rounded-lg border border-gray-800">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Bugün bu salonda gösterim bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {movies.map((movie) => (
                <div key={movie._id} className="bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-lg p-6 border border-blue-500/20 hover:border-purple-500/40 hover:from-blue-500/20 hover:to-purple-600/20 transition-all">
                  <div className="flex gap-6">
                    {/* Movie Poster */}
                    <Link 
                      to={`/film/${movie.slug || movie._id}`}
                      className="flex-shrink-0"
                    >
                      <img
                        src={movie.poster || 'https://via.placeholder.com/200x300?text=No+Image'}
                        alt={movie.title}
                        className="w-32 h-48 object-cover rounded-lg hover:opacity-80 transition-opacity"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/200x300?text=No+Image';
                        }}
                      />
                    </Link>

                    {/* Movie Info */}
                    <div className="flex-grow">
                      <Link 
                        to={`/film/${movie.slug || movie._id}`}
                      >
                        <h3 className="text-2xl font-bold text-white mb-3 hover:text-[#FDB714] transition-colors cursor-pointer">
                          {movie.title}
                        </h3>
                      </Link>
                      
                      {/* Formats, Languages and Showtimes */}
                      <div className="space-y-4">
                        {Object.entries(movie.showtimesByFormatAndLanguage).map(([formatAndLang, times]) => (
                          <div key={formatAndLang}>
                            <div className="flex items-center gap-2 mb-3">
                              <Clock size={18} className="text-[#FDB714]" />
                              <span className="text-[#FDB714] font-semibold">
                                {formatAndLang}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {times.sort().map((time, index) => (
                                <span
                                  key={index}
                                  className="bg-gradient-to-r from-orange-500/80 to-red-500/80 border border-orange-500/30 text-white px-4 py-2 rounded text-sm font-medium hover:from-orange-600 hover:to-red-600 transition-all cursor-pointer"
                                >
                                  {time}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SalonDetail;
